import type { FC } from "react";
import {
  type CarDescriptionProps as ApacCarDescriptionProps,
  CoreCarsComponent,
  getVehicleClassLabelFromVehicle,
} from "@apac/components/cars";
import { useModuleBEM } from "@b2bportal/core-themes";
import { FuelType } from "@b2bportal/ground-api";
import { Trans, useI18nContext } from "@hopper-b2b/i18n";
import clsx from "clsx";
import styles from "./CarDescription.module.scss";

export type CarDescriptionProps = ApacCarDescriptionProps & {
  hasUnlimitedMileage?: boolean;
  collisionDamageWaiver?: boolean;
};

export const CarDescription: FC<CarDescriptionProps> = ({
  supplier,
  vehicle,
  className,
  showImage = true,
  showStoreInfo = true,
  showTags = true,
  isFreeCancellation = false,
  hasUnlimitedMileage = false,
  collisionDamageWaiver = false,
}) => {
  const [block, cn] = useModuleBEM(styles, CoreCarsComponent.CarDescription);
  const { t } = useI18nContext();

  return (
    <div className={clsx(block, className)}>
      <div className={cn("title")}>
        <span className={cn("vehicle-class")}>
          {getVehicleClassLabelFromVehicle(t, vehicle)}
        </span>
        {FuelType.Unspecified !== vehicle.fuelType && (
          <div className={cn("fuel-type")}>{vehicle.fuelType}</div>
        )}
      </div>
      {!!supplier.logo && (
        <div className={cn("store-info", { show: showStoreInfo })}>
          <img src={supplier.logo} alt={supplier.name} />
        </div>
      )}
      <div className={cn("subtitle")}>
        {/* Will handle adding "or similar" suffix in backend, api not available now: falling back to contructing string in FE */}
        {/* @ts-expect-error: temporary ignore before api updated */}
        {vehicle.styledName ?? (
          <Trans
            i18nKey={t("core-cars.carCard.name", { name: vehicle.name })}
            components={{
              name: <span className={cn("name")} />,
            }}
          />
        )}
      </div>
      <img
        className={cn("image", { show: showImage })}
        // TODO: use a double check image size on production and see if small size is enough
        src={vehicle.images?.original}
        alt={vehicle.name}
        loading="lazy"
      />
      <div
        className={cn("tags", {
          show: showTags && (isFreeCancellation || hasUnlimitedMileage),
        })}
      >
        {isFreeCancellation ? (
          <div className={cn("tag")}>{t("apac.cars.freeCancellation")}</div>
        ) : null}
        {hasUnlimitedMileage ? (
          <div className={cn("tag")}>{t("apac.cars.unlimitedMileage")}</div>
        ) : null}
        {collisionDamageWaiver ? (
          <div className={cn("tag")}>
            {t("apac.cars.collisionDamageWaiver")}
          </div>
        ) : null}
      </div>
    </div>
  );
};
